import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, ChevronRight, X, Sparkles } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { unitsAPI, announcementsAPI } from '../services/api';

/**
 * Onboarding checklist v2 — PER SEGMENT (Jaime-approved table 2026-10-01).
 * Launch modules only: Mensajes, Quejas y Sugerencias, Mi acceso, Compras,
 * Tareas, Profile, Muro, Anuncios, Pánico, Reglamentos. No payments/pets/
 * vehicles/equipment/budget steps.
 *
 * Segment detection (role + context shape): admin/sub_admin with condo → A1
 * (or A2 while the condo has 0 units); propietario with condo-linked unit →
 * B1; propietario standalone-only → D; resident/familiar/inquilino → B2;
 * empleado → C. Ambiguous shapes default to B2 (failure protocol).
 *
 * Persistence: localStorage per segment (vecindus.onboarding.v2.<segment>),
 * same dismissal semantics as v1. Real actions auto-complete steps: pages
 * dispatch window event 'vecindus:onboarding-done' with the step id
 * (e.g. HomeTasks dispatches 'task', BuildingUnits dispatches 'units');
 * the admin 'units' step also auto-completes when the condo already has
 * units at mount.
 */

type Segment = 'admin' | 'admin_link' | 'propietario' | 'residente' | 'empleado' | 'standalone';

interface Step { id: string; label: string; desc: string; to: string }
interface SegmentDef { title: string; steps: Step[]; hint?: string }

const SEGMENTS: Record<Segment, SegmentDef> = {
  admin: {
    title: 'Primeros pasos',
    steps: [
      { id: 'units', label: 'Crea las unidades', desc: 'Registra los apartamentos o casas', to: '/building/units' },
      { id: 'invite', label: 'Invita a los residentes', desc: 'Propietarios y familiares', to: '/building/invites' },
      { id: 'aviso', label: 'Publica tu primer aviso', desc: 'En el muro del condominio', to: '/building/announcements' },
      { id: 'guard', label: 'Configura el acceso del guardia', desc: 'Autoriza el escaneo de QR', to: '/building/verify' },
    ],
  },
  admin_link: {
    title: 'Conecta tu primera unidad',
    steps: [
      { id: 'invite', label: 'Invita a los propietarios', desc: 'Comparte el vínculo de ocupación para que registren su unidad', to: '/building/invites' },
      { id: 'units', label: 'Vincula las unidades', desc: 'Cuando el propietario comparta su código de vínculo, vincúlala aquí', to: '/building/units' },
    ],
  },
  propietario: {
    title: 'Primeros pasos',
    steps: [
      { id: 'profile', label: 'Completa tu perfil', desc: 'Tu información y tu unidad', to: '/profile' },
      { id: 'task', label: 'Crea tu primera tarea', desc: 'Organiza tu hogar', to: '/tasks' },
      { id: 'qr', label: 'Prueba tu acceso QR', desc: 'Tu llave digital de acceso', to: '/my-qr' },
      { id: 'invite', label: 'Invita a los Residentes/Familiar de tu unidad', desc: 'Compárteles su acceso', to: '/building/invites' },
    ],
  },
  residente: {
    title: 'Primeros pasos',
    steps: [
      { id: 'profile', label: 'Completa tu perfil', desc: 'Tu información personal', to: '/profile' },
      { id: 'task', label: 'Crea tu primera tarea', desc: 'Organiza tu hogar', to: '/tasks' },
      { id: 'qr', label: 'Prueba tu acceso QR', desc: 'Tu llave digital de acceso', to: '/my-qr' },
      { id: 'shopping', label: 'Agrega un producto a la lista de compras', desc: 'Lo que no debe faltar en casa', to: '/shopping' },
    ],
  },
  empleado: {
    title: 'Primeros pasos',
    steps: [
      { id: 'profile', label: 'Completa tu perfil', desc: 'Tu información personal', to: '/profile' },
      { id: 'task', label: 'Revisa tus tareas asignadas', desc: 'Lo que tienes pendiente', to: '/tasks' },
      { id: 'shopping', label: 'Agrega un producto a la lista de compras', desc: 'Lo que necesita el condominio', to: '/shopping' },
    ],
  },
  standalone: {
    title: 'Primeros pasos',
    steps: [
      { id: 'profile', label: 'Completa tu perfil', desc: 'Tu información y tu unidad', to: '/profile' },
      { id: 'task', label: 'Crea tu primera tarea', desc: 'Organiza tu hogar', to: '/tasks' },
      { id: 'invite', label: 'Invita a los Residentes/Familiar de tu unidad', desc: 'Compárteles su acceso', to: '/building/invites' },
    ],
  },
};

function detectSegment(role: string | undefined | null, condoId: string | undefined, ownedUnits: any[] | undefined): Segment {
  const units = ownedUnits || [];
  const ownsLinked = units.some((u) => u.condo_id);
  const ownsStandalone = units.some((u) => !u.condo_id && !u.assigned);
  if (['empleado', 'security', 'maintenance_staff'].includes(role || '')) return 'empleado';
  if (['admin', 'sub_admin', 'super_admin'].includes(role || '') && condoId) return 'admin';
  if (role === 'propietario') {
    if (condoId || ownsLinked) return 'propietario';
    if (ownsStandalone) return 'standalone';
  }
  // Failure protocol: ambiguous account shape defaults to B2 (residente).
  return 'residente';
}

interface OnboardingState { dismissed: boolean; done: string[] }

function storageKey(segment: Segment) { return `vecindus.onboarding.v2.${segment}`; }

function load(segment: Segment): OnboardingState {
  try {
    const raw = localStorage.getItem(storageKey(segment));
    if (raw) {
      const parsed = JSON.parse(raw);
      return { dismissed: Boolean(parsed.dismissed), done: Array.isArray(parsed.done) ? parsed.done : [] };
    }
  } catch { /* corrupted state → fresh start */ }
  return { dismissed: false, done: [] };
}

export default function OnboardingChecklist() {
  const { user, ownedUnits } = useAuth();
  // Auth hydration: ownedUnits may still be empty on first render (bug #89
  // lesson) — only settle the segment once contexts have loaded.
  const [ready, setReady] = useState(false);
  useEffect(() => { setReady(true); }, []);
  const segment = detectSegment(user?.role, user?.condoId || undefined, ownedUnits);
  const def = SEGMENTS[segment];
  const [state, setState] = useState<OnboardingState>(() => load('residente'));
  // Re-load persisted state once the segment is stable (auth hydrated).
  useEffect(() => { if (ready) setState(load(segment)); }, [ready, segment]);
  const hidden = state.dismissed || def.steps.every((s) => state.done.includes(s.id));

  // Admin A1: auto-complete 'Crea las unidades' when the condo already has units.
  useEffect(() => {
    if (segment !== 'admin' || !user?.condoId) return;
    let cancelled = false;
    unitsAPI.getAll(user.condoId)
      .then((res) => {
        if (cancelled) return;
        const data = res.data?.data;
        const units = data?.units || (Array.isArray(data) ? data : res.data?.units || []);
        if (units.length > 0) markDone('units');
      })
      .catch(() => undefined);
    return () => { cancelled = true; };
  }, [segment, user?.condoId]);

  // B5 (demo-review 2026-10-06): the demo admin's condo is pre-seeded with
  // announcements — 'Publica tu primer aviso' is already true there. Without
  // this the checklist showed 0/4 despite the seeded muro, which read as a
  // broken demo. Same shape as the units auto-complete above.
  useEffect(() => {
    if (segment !== 'admin' || !user?.condoId) return;
    let cancelled = false;
    announcementsAPI.getAll(user.condoId)
      .then((res) => {
        if (cancelled) return;
        const data = res.data?.data;
        const items = data?.announcements || (Array.isArray(data) ? data : res.data?.announcements || []);
        if (items.length > 0) markDone('aviso');
      })
      .catch(() => undefined);
    return () => { cancelled = true; };
  }, [segment, user?.condoId]);

  const persist = (next: OnboardingState) => {
    try { localStorage.setItem(storageKey(segment), JSON.stringify(next)); } catch { /* ignore */ }
    setState(next);
  };

  const markDone = (id: string) => {
    setState((prev) => {
      if (prev.done.includes(id)) return prev;
      const next = { ...prev, done: [...prev.done, id] };
      try { localStorage.setItem(storageKey(segment), JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  };

  useEffect(() => {
    const onDone = (e: Event) => {
      const id = (e as CustomEvent<string>).detail;
      if (!id) return;
      markDone(id);
    };
    window.addEventListener('vecindus:onboarding-done', onDone);
    return () => window.removeEventListener('vecindus:onboarding-done', onDone);
  }, []);

  const dismiss = () => persist({ ...state, dismissed: true });

  if (!ready || hidden) return null;

  return (
    <div data-testid="onboarding-checklist" className="mb-6 rounded-2xl border border-[var(--v-border-light)] bg-[var(--v-surface-1)] p-5 shadow-sm">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-[#36CCD8]" />
          <h2 className="text-lg font-bold text-[var(--v-text-primary)]">{def.title}</h2>
          <span className="rounded-full bg-[#36CCD8]/10 px-2 py-0.5 text-xs font-semibold text-[#36CCD8]">
            {state.done.length}/{def.steps.length}
          </span>
        </div>
        <button onClick={dismiss} aria-label="Descartar" className="rounded-lg p-1.5 text-[var(--v-text-secondary)] hover:bg-[var(--v-surface-2)]">
          <X className="h-4 w-4" />
        </button>
      </div>
      <ul className="grid gap-2 sm:grid-cols-2">
        {def.steps.map((step) => {
          const done = state.done.includes(step.id);
          return (
            <li key={step.id}>
              <Link
                to={step.to}
                onClick={() => markDone(step.id)}
                className={`flex items-center gap-3 rounded-xl border p-3 transition-colors ${done ? 'border-[#36CCD8]/40 bg-[#36CCD8]/5 opacity-70' : 'border-[var(--v-border-light)] hover:bg-[var(--v-surface-2)]'}`}
              >
                {done
                  ? <CheckCircle2 className="h-5 w-5 flex-shrink-0 text-[#36CCD8]" />
                  : <span className="h-5 w-5 flex-shrink-0 rounded-full border-2 border-[var(--v-border-light)]" />}
                <span className="min-w-0 flex-1">
                  <span className={`block text-sm font-semibold ${done ? 'line-through text-[var(--v-text-secondary)]' : 'text-[var(--v-text-primary)]'}`}>{step.label}</span>
                  <span className="block truncate text-xs text-[var(--v-text-secondary)]">{step.desc}</span>
                </span>
                <ChevronRight className="h-4 w-4 flex-shrink-0 text-[var(--v-text-secondary)]" />
              </Link>
            </li>
          );
        })}
      </ul>
      {segment === 'standalone' && (
        <p className="mt-3 text-xs text-[var(--v-text-secondary)]">Crea o vínculate a un condominio cuando quieras.</p>
      )}
      {segment === 'admin_link' && (
        <p className="mt-3 text-xs text-[var(--v-text-secondary)]">Vínculo de ocupación: cuando el propietario registre su unidad, comparte su código de vínculo con la administración para sumarla al condominio.</p>
      )}
    </div>
  );
}
